<script setup>
import { h, ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
} from '@tanstack/vue-table';
import { format } from 'date-fns';

import NextButton from 'dashboard/components-next/button/Button.vue';
import FluentIcon from 'shared/components/FluentIcon/DashboardIcon.vue';

import StandbiesApi from 'dashboard/api/atendeTotal/standbies';
import ThumbnailGroup from 'dashboard/components/widgets/ThumbnailGroup.vue';
import Table from 'dashboard/components/table/Table.vue';
import AtendeTotalEditStandbyTeamDistribution from '../atendeTotal/EditStandbyTeamDistribution.vue';

// State
const showEditStandbyTeamDistributionPopup = ref(false);
const showCancelStandbyPopup = ref(false);
const currentStandby = ref({});

const route = useRoute();
const store = useStore();

// Computed
const teamId = computed(() => route.params.teamId);

const teamMembers = computed(() =>
  store.getters['teamMembers/getTeamMembers'](teamId.value)
);

const standbies = computed(() => store.getters['standbies/getStandbies']);

// Status mappings
const statusIconMap = {
  scheduled: 'calendar-clock',
  running: 'calendar-empty',
  done: 'checkmark-circle',
  canceled: 'dismiss-circle',
};

const statusTranslationMap = {
  scheduled: 'Agendado',
  running: 'Em andamento',
  done: 'Concluído',
  canceled: 'Cancelado',
};

// Helper functions
const formatDateTime = datetimeStr => {
  return format(new Date(datetimeStr), 'dd/MM/yyyy HH:mm');
};

const findTeamMember = userId => {
  return teamMembers.value.find(member => member.id === userId);
};

const getUsersList = teamMembersIds => {
  return teamMembersIds.map(userId => {
    const { id, name, avatar } = findTeamMember(userId);
    return { id, name, avatar };
  });
};

// Methods
const loadData = async () => {
  await store.dispatch('agents/get');
  await store.dispatch('teamMembers/get', { teamId: teamId.value });
  await store.dispatch('standbies/get', teamId.value);
};

const openEditStandbyTeamDistributionPopup = standby => {
  showEditStandbyTeamDistributionPopup.value = true;
  currentStandby.value = standby;
};

const hideEditStandbyTeamDistributionPopup = () => {
  showEditStandbyTeamDistributionPopup.value = false;
  currentStandby.value = {};
};

const openCancelStandbyPopup = standby => {
  showCancelStandbyPopup.value = true;
  currentStandby.value = standby;
};

const closeCancelStandbyPopup = () => {
  showCancelStandbyPopup.value = false;
  currentStandby.value = {};
};

const onCancelStandby = async () => {
  await StandbiesApi.cancel(teamId.value, currentStandby.value.id);
  await loadData();
  closeCancelStandbyPopup();
};

// Table data
const tableData = computed(() => {
  return standbies.value.map(standby => {
    const datetime = `${formatDateTime(standby.start_at)} - ${formatDateTime(standby.end_at)}`;

    return {
      ...standby,
      datetime,
      created_at: formatDateTime(standby.created_at),
    };
  });
});

// Column definitions
const columnHelper = createColumnHelper();

const columns = [
  columnHelper.accessor('status', {
    header: 'Status',
    size: 250,
    meta: { align: 'center' },
    enableSorting: false,
    cell: cellProps => {
      const status = cellProps.getValue();
      const icon = statusIconMap[status];
      const translationStatus = statusTranslationMap[status];

      return h('span', { class: 'flex items-center justify-center' }, [
        h(FluentIcon, {
          class: '!mr-1',
          icon,
          size: 16,
        }),
        h('span', {}, translationStatus),
      ]);
    },
  }),
  columnHelper.accessor('datetime', {
    header: 'Data',
    size: 300,
    meta: { align: 'center' },
    enableSorting: false,
    cell: cellProps => {
      return h('span', { class: 'flex justify-center' }, cellProps.getValue());
    },
  }),
  columnHelper.accessor('agents_ids', {
    header: 'Agentes de plantão',
    size: 300,
    meta: { align: 'center' },
    enableSorting: false,
    cell: cellProps => {
      const agentsIds = cellProps.getValue();
      return h(ThumbnailGroup, {
        class: 'justify-self-center',
        size: 30,
        usersList: getUsersList(agentsIds),
      });
    },
  }),
  columnHelper.accessor('created_at', {
    header: 'Criado em',
    size: 300,
    meta: { align: 'center' },
    enableSorting: false,
    cell: cellProps => {
      return h('span', { class: 'flex justify-center' }, cellProps.getValue());
    },
  }),
  columnHelper.accessor('id', {
    header: 'Ações',
    size: 300,
    meta: { align: 'center' },
    enableSorting: false,
    cell: cellProps => {
      const row = cellProps.row.original;
      const isEditDisabled = row.status !== 'scheduled';
      const isCancelDisabled = ['canceled', 'done'].includes(row.status);

      return h('div', { class: 'flex space-x-2 justify-self-center' }, [
        h(NextButton, {
          label: 'Editar',
          icon: 'i-lucide-pencil',
          size: 'sm',
          variant: 'faded',
          color: 'blue',
          disabled: isEditDisabled,
          onClick: () => openEditStandbyTeamDistributionPopup(row),
        }),
        h(NextButton, {
          label: 'Cancelar',
          icon: 'i-lucide-circle-x',
          size: 'sm',
          variant: 'faded',
          color: 'ruby',
          disabled: isCancelDisabled,
          onClick: () => openCancelStandbyPopup(row),
        }),
      ]);
    },
  }),
];

// Table instance
const table = useVueTable({
  get data() {
    return tableData.value;
  },
  columns,
  getCoreRowModel: getCoreRowModel(),
});

// Lifecycle
onMounted(() => {
  loadData();
});
</script>

<template>
  <section class="flex-1 h-full overflow-auto bg-white dark:bg-slate-900">
    <section class="overflow-x-auto">
      <Table :table="table" />
    </section>

    <woot-modal
      v-if="showEditStandbyTeamDistributionPopup"
      v-model:show="showEditStandbyTeamDistributionPopup"
      :on-close="hideEditStandbyTeamDistributionPopup"
    >
      <AtendeTotalEditStandbyTeamDistribution
        :standby-to-edit="currentStandby"
        :on-close="hideEditStandbyTeamDistributionPopup"
      />
    </woot-modal>

    <!-- Cancel Standby -->
    <woot-delete-modal
      v-model:show="showCancelStandbyPopup"
      :on-close="closeCancelStandbyPopup"
      :on-confirm="onCancelStandby"
      :title="$t('TEAMS_SETTINGS.STANDBIES.CANCEL.TITLE')"
      :message="$t('TEAMS_SETTINGS.STANDBIES.CANCEL.MESSAGE')"
      :confirm-text="$t('TEAMS_SETTINGS.STANDBIES.CANCEL.CONFIRM')"
      :reject-text="$t('TEAMS_SETTINGS.STANDBIES.CANCEL.REJECT')"
    />
  </section>
</template>
